import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { FaSearch, FaSave, FaGlobe, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const LANGUAGES = {
  de: { name: 'Tysk', flag: '🇩🇪' },
  fr: { name: 'Fransk', flag: '🇫🇷' },
  en: { name: 'Engelsk (UK)', flag: '🇬🇧' },
  en_us: { name: 'Engelsk (US)', flag: '🇺🇸' }
};

const SECTIONS = {
  all: { name: 'Alle sektioner', icon: '📋' },
  ui: { name: 'UI & Interface', icon: '🎨' },
  recipeContent: { name: 'Opskrifter', icon: '📖' },
  ingredients: { name: 'Ingredienser', icon: '🥕' }
};

const AdminTranslationsPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [selectedSection, setSelectedSection] = useState('all');
  const [translations, setTranslations] = useState([]);
  const [allEditedTranslations, setAllEditedTranslations] = useState({}); // { de: {...}, fr: {...}, en: {...} }
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Redirect non-admin users
  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Kun administratorer kan tilgå denne side');
      navigate('/settings');
    }
  }, [isAdmin, navigate]);

  // Load translations when language changes
  useEffect(() => {
    if (selectedLanguage) {
      loadTranslations(selectedLanguage);
    }
  }, [selectedLanguage]);

  const loadTranslations = async (lang) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/translations/editor?language=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      setTranslations(response.data.translations);
      // Don't clear edited translations when switching languages
    } catch (error) {
      console.error('Error loading translations:', error);
      toast.error('Kunne ikke indlæse oversættelser');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationChange = (key, value) => {
    setAllEditedTranslations(prev => ({
      ...prev,
      [selectedLanguage]: {
        ...(prev[selectedLanguage] || {}),
        [key]: value
      }
    }));
  };

  const getCurrentTranslation = (item) => {
    // Return edited value if exists for current language, otherwise original translation
    const languageEdits = allEditedTranslations[selectedLanguage] || {};
    return languageEdits.hasOwnProperty(item.key) 
      ? languageEdits[item.key] 
      : item.translation;
  };
  
  const hasChanges = () => {
    return Object.keys(allEditedTranslations).length > 0;
  };
  
  const getChangedLanguagesCount = () => {
    return Object.keys(allEditedTranslations).length;
  };

  const saveAllTranslations = async () => {
    setSaving(true);
    const changedLanguages = Object.keys(allEditedTranslations);
    
    try {
      const token = localStorage.getItem('session_token');
      let savedCount = 0;
      
      // Save each language that has changes
      for (const lang of changedLanguages) {
        // Load that language's full translations
        const response = await axios.get(`${API}/admin/translations/editor?language=${lang}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        // Build final translations (merge original + edited for this language)
        const finalTranslations = {};
        response.data.translations.forEach(item => {
          const languageEdits = allEditedTranslations[lang] || {};
          finalTranslations[item.key] = languageEdits.hasOwnProperty(item.key)
            ? languageEdits[item.key]
            : item.translation;
        });
        
        // Save this language
        await axios.put(
          `${API}/admin/translations/editor/${lang}`,
          { translations: finalTranslations },
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );
        
        savedCount++;
      }
      
      toast.success(`✅ Gemt oversættelser for ${savedCount} sprog: ${changedLanguages.map(l => LANGUAGES[l].name).join(', ')}`);
      setAllEditedTranslations({});
      
      // Reload current language
      await loadTranslations(selectedLanguage);
    } catch (error) {
      console.error('Error saving translations:', error);
      toast.error('Kunne ikke gemme oversættelser');
    } finally {
      setSaving(false);
    }
  };

  // Filter translations based on section and search query
  const filteredTranslations = translations.filter(item => {
    // Section filter
    if (selectedSection !== 'all') {
      if (selectedSection === 'ui') {
        // UI section = everything except recipeContent and ingredients
        if (item.key.startsWith('recipeContent.') || item.key.startsWith('ingredients.')) {
          return false;
        }
      } else {
        // Specific section
        if (!item.key.startsWith(selectedSection + '.')) {
          return false;
        }
      }
    }
    
    // Search filter
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      item.key.toLowerCase().includes(query) ||
      item.master.toLowerCase().includes(query) ||
      item.translation.toLowerCase().includes(query) ||
      getCurrentTranslation(item).toLowerCase().includes(query)
    );
  });

  if (!isAdmin()) {
    return null;
  }

  return (
    <div className="space-y-6 fade-in pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <FaGlobe className="text-3xl" />
          <h1 className="text-4xl font-bold">Oversættelseseditor</h1>
        </div>
        <p className="text-white/90">
          Rediger alle oversættelser nemt og hurtigt. Vælg et sprog og ret de tekster, du ønsker.
        </p>
      </div>

      {/* Language Tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <span className="font-semibold text-gray-700">Vælg sprog:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(LANGUAGES).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => setSelectedLanguage(code)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedLanguage === code
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section Filter */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <span className="font-semibold text-gray-700">Vælg sektion:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SECTIONS).map(([code, section]) => (
            <button
              key={code}
              onClick={() => setSelectedSection(code)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedSection === code
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="text-2xl">{section.icon}</span>
              <span>{section.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search Box */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Søg i oversættelser (nøgle, dansk tekst eller oversættelse)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-4 py-3 text-lg"
          />
        </div>
        <div className="text-sm text-gray-600 mt-2">
          {searchQuery ? (
            <p>
              Viser {filteredTranslations.length} af {translations.length} oversættelser
              {selectedSection !== 'all' && ` i ${SECTIONS[selectedSection].name}`}
            </p>
          ) : (
            <p>
              {filteredTranslations.length} oversættelser i {SECTIONS[selectedSection].name}
            </p>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="flex flex-col items-center gap-4">
            <FaSpinner className="animate-spin text-blue-500 text-5xl" />
            <p className="text-gray-600">Indlæser oversættelser...</p>
          </div>
        </div>
      )}

      {/* Translations List */}
      {!loading && filteredTranslations.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="space-y-6">
            {filteredTranslations.map((item, index) => (
              <div
                key={item.key}
                className="border-b border-gray-200 pb-6 last:border-b-0"
              >
                {/* Key Label */}
                <div className="mb-2">
                  <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {item.key}
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Master (Danish) - Read-only */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Master (Dansk):
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-800">{item.master}</p>
                    </div>
                  </div>

                  {/* Translation - Editable */}
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      {LANGUAGES[selectedLanguage].name} (redigerbar):
                    </label>
                    <Textarea
                      value={getCurrentTranslation(item)}
                      onChange={(e) => handleTranslationChange(item.key, e.target.value)}
                      className="min-h-[80px] resize-y"
                      placeholder={`Oversættelse til ${LANGUAGES[selectedLanguage].name}...`}
                    />
                  </div>
                </div>

                {/* Show indicator if edited */}
                {allEditedTranslations[selectedLanguage]?.hasOwnProperty(item.key) && (
                  <div className="mt-2">
                    <span className="text-xs text-green-600 font-semibold">
                      ✓ Ændret (endnu ikke gemt)
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && filteredTranslations.length === 0 && translations.length > 0 && (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
          <p className="text-gray-500 text-lg">
            Ingen oversættelser matchede din søgning "{searchQuery}"
          </p>
        </div>
      )}

      {/* Fixed Save Button at Bottom */}
      {!loading && translations.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              {hasChanges() ? (
                <p className="text-sm text-orange-600 font-semibold">
                  ⚠️ Du har ugemte ændringer i {getChangedLanguagesCount()} sprog: {
                    Object.keys(allEditedTranslations).map(lang => LANGUAGES[lang].flag).join(' ')
                  }
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  Alle ændringer er gemt
                </p>
              )}
            </div>
            <Button
              onClick={saveAllTranslations}
              disabled={saving || !hasChanges()}
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 text-lg font-bold shadow-md"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Gemmer...
                </>
              ) : (
                <>
                  <FaSave className="mr-2" />
                  💾 Gem alle ændringer ({getChangedLanguagesCount()} sprog)
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTranslationsPage;
